import { lazy, Suspense, useEffect, useState } from "react"
import TagManager from "react-gtm-module"
import { QueryClient, QueryClientProvider } from "react-query"
// import { LandingPage } from "@fuse/pages/landingPage";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

import { StripeProvider } from "@fuse/customProviders/StripeProvider"
import { EFeatureFlags, TWebUser } from "@letshang/constants"
import { clarity } from "clarity-js"
import { signOut, User } from "firebase/auth"
import { size } from "lodash-es"
import { useRecoilState, useRecoilValue } from "recoil"
import withProviders from "src/withProviders"

import ProtectedRouteWrapper from "@navigation/ProtectedRouteWrapper"

import {
  defaultUserInfoWithBuddies,
  userInfoWithBuddiesState,
} from "@fuse/recoil/buddies"
import {
  requestedSuggestedBuddiesAtom,
  suggestedBuddiesAtom,
} from "@fuse/recoil/buddies"
import { defaultUser, userState } from "@fuse/recoil/signup"

import { auth } from "@firebaseConfig/clientApp"

import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"
import { DEV } from "@settingsAlias/settings"

import { getUserInfo } from "@api/firebaseWrappedApis"

import { isDev } from "@fuse/utils/DeviceDection"

import { useDevEmailVerification } from "@fuse/customHooks/useDevEmailVerification"
import useTrackUser from "@fuse/customHooks/useTrackUser"

import { DevEmailIndicator } from "@fuse/components/DevEmailIndicator"

import { ClaimTicketLandingPage } from "@fuse/pages/claimTicket"
import CompleteProfile from "@fuse/pages/completeProfile"
import DownloadApp from "@fuse/pages/downloadApp"
import { VerifyEmail } from "@fuse/pages/emailVerification/verifyEmail"
import { EventLandingPage } from "@fuse/pages/eventPage"
import CreateEventPage from "@fuse/pages/eventPage/createEvent"
import CreateEventPageV2 from "@fuse/pages/eventPage/createEvent-v2"
import CreateEventPageV3 from "@fuse/pages/eventPage/createEvent-v3"
import CreateEventPageV4 from "@fuse/pages/eventPage/createEvent-v4"
import CreateEventPageV5 from "@fuse/pages/eventPage/createEvent-v5"
import EventCreateSuccess from "@fuse/pages/eventPage/createEvent/EventCreateSuccess"
import EditEvent from "@fuse/pages/eventPage/eventManagement-hackathon-4/EditEvent"
import EventRoulette from "@fuse/pages/eventRoullete"
import EventSignUp from "@fuse/pages/eventSignUp"
import ChooseTicket from "@fuse/pages/eventSignUp/BuyTickets/ChooseTicket"
import FillFriendsTicketInfo from "@fuse/pages/eventSignUp/BuyTickets/FillFriendsTicketInfo"
import FillQuestionnaire from "@fuse/pages/eventSignUp/BuyTickets/FillQuestionnaire"
import FillQuestionnaireUpdated from "@fuse/pages/eventSignUp/BuyTickets/FillQuestionnaireUpdated"
import StripeCheckout from "@fuse/pages/eventSignUp/BuyTickets/StripeCheckout"
import StripeSuccess from "@fuse/pages/eventSignUp/BuyTickets/StripeSuccess"
import MyEventTicket from "@fuse/pages/eventSignUp/MyEventTicket"
import IndividualTicket from "@fuse/pages/eventSignUp/MyEventTicket/IndividualTicket"
import TicketCollection from "@fuse/pages/eventSignUp/MyEventTicket/TicketCollection"
import EventTemplates from "@fuse/pages/eventTemplates"
import Explore from "@fuse/pages/explore"
import { GroupLandingPage } from "@fuse/pages/groupPage"
import LandingPage from "@fuse/pages/landingPage/index"
import NotImplementedPage from "@fuse/pages/NotImplementedPage"
import OrganizationProfile from "@fuse/pages/organizations"
import PeoplePage from "@fuse/pages/people"
import PluginDetailPage from "@fuse/pages/plugins/PluginDetailPage"
// Import our new PluginDevelopmentStudio component
import PluginDevelopmentStudio from "@fuse/pages/plugins/views/PluginDevelopmentStudio"
import ProfilePage from "@fuse/pages/profilePage"
import SignUpPage from "@fuse/pages/signUp"
import SpecialEvent from "@fuse/pages/specialEvent"

import "./App.css"

export const ROUTES = {
  LANDING: "/",
  DOWNLOAD_APP: "/downloadApp",
  EVENTS: "/events/:eventId",
  SHARE_EVENTS: "share/events/:eventId",
  GROUPS: "/groups/*",
  SIGNUP: "/signup",
  PROFILE: "/profile",
  EVENT_SIGNUP: "/eventSignup",
  CHOOSE_TICKET: "/events/:eventId/eventSignup/chooseTicket",
  FILL_FRIENDS_TICKET_INFO:
    "/events/:eventId/eventSignup/fillFriendsTicketInfo",
  FILL_QUESTIONNAIRE: "/events/:eventId/eventSignup/fillQuestionnaire",
  FILL_QUESTIONNAIRE_UPDATED:
    "/events/:eventId/eventSignup/fillQuestionnairev2",
  STRIPE_CHECKOUT: "/events/:eventId/eventSignup/stripeCheckout",
  STRIPE_SUCCESS: "/events/:eventId/eventSignup/stripeSuccess",
  MY_TICKET: "/events/:eventId/tickets/:attendeeId",
  VERIFY_EMAIL: "/verify/email",
  EVENT_ROULLETE: "/event-roulette",
  EXPLORE: "/explore",
  SPECIAL_EVENT: "/special-event",
  PEOPLE: "/people",
  // Add organization routes
  ORGANIZATIONS: "/organizations/:orgId",
  SHARE_ORGANIZATIONS: "share/organizations/:orgId",
  // Add other routes here as needed
  CLAIM_TICKET: "/claim_ticket/:eventId/:phoneNumber",
  // group-ticket view
  TICKETS: "/events/:eventId/tickets",
  MY_TICKET_ID: ":attendeeId",
  SEARCH: "/search",
  CREATE_EVENT: "/create-event",
  CREATE_EVENT_FAST_SIGN_UP: "/create-event-v2",
  CREATE_EVENT_V3: "/create-event-v3",
  CREATE_EVENT_V4: "/create-event-v4",
  CREATE_EVENT_V5: "/create-event-v5",
  EVENT_CREATE_SUCCESS: "/create-event/success",
  EVENT_TEMPLATES: "/event-templates",
  COMPLETE_PROFILE: "/complete-profile",
  // Plugin routes
  PLUGIN_DETAILS: "/plugins/:pluginId",
  PLUGIN_STUDIO_CREATION: "/plugin-studio/create",
  EDIT_EVENT: "/edit-event/:eventId",
}

const queryClient = new QueryClient()

const RedirectTo = () => {
  const location = useLocation()
  const newPath = location.pathname.replace("share/", "")
  return <Navigate to={`${newPath}`} replace />
}

function App() {
  const [user, setUser] = useRecoilState(userState)
  const [userInfoWithBuddies, setUserInfoWithBuddies] = useRecoilState(
    userInfoWithBuddiesState,
  )
  const suggestedBuddiesAtomData = useRecoilValue(suggestedBuddiesAtom)
  const getRequestedSuggestedBuddies = useRecoilValue(
    requestedSuggestedBuddiesAtom,
  )
  const requestedSuggestedBuddiesCount = size(getRequestedSuggestedBuddies)

  useEffect(() => {
    const handleUnload = () => {
      localStorage.removeItem("savedEventDetails")
    }

    window.addEventListener("beforeunload", handleUnload)

    return () => {
      window.removeEventListener("beforeunload", handleUnload)
    }
  }, [])

  useTrackUser()
  useDevEmailVerification() // 🔥 DEV: Restore fake email verification state

  // 🔥 DEV: Inform developers about fake email verification
  useEffect(() => {
    if (isDev()) {
      console.log(
        "%c🚀 DEV MODE: Fake Email Verification Available!",
        "color: #22c55e; font-weight: bold; font-size: 14px;",
      )
      console.log(
        "%c   • Click 'Verify' button → instantly verified (no real email needed)",
        "color: #10b981; font-size: 12px;",
      )
      console.log(
        "%c   • Verification persists until logout",
        "color: #10b981; font-size: 12px;",
      )
      console.log(
        "%c   • Look for green indicator in bottom-right when active",
        "color: #10b981; font-size: 12px;",
      )
    }
  }, [])

  const tagManagerArgs = {
    gtmId: "GTM-MCFWPZPN",
    uid: user?.uid,
  }

  TagManager.initialize(tagManagerArgs)

  // clarity.start({
  //   projectId: "mym1lhs5lb",
  //   upload: "https://m.clarity.ms/collect",
  //   track: true,
  //   content: true,
  // })

  // if (user?.uid) clarity.identify(user?.uid)
  // clarity.set("dev", DEV.toString())

  // useEffect(() => {
  //   const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
  //     if (!currentUser) {
  //        signOut(auth)
  //        setUser(defaultUser)
  //        setUserInfoWithBuddies(defaultUserInfoWithBuddies)
  //     }
  //   })

  //   return () => unsubscribe()
  // }, [])

  useEffect(() => {
    // If user is logged in, get user info
    if (user) {
      getUserInfo({}).then((res: any) => {
        setUser((oldUser: TWebUser) => {
          // Preserve local country value if backend returns null or empty
          const backendCountry = res?.userInfo?.country || ""
          const localCountry = oldUser?.country || ""

          return {
            ...oldUser,
            ...res?.userInfo,
            // Only update country if backend has a valid value, otherwise keep local value
            country: backendCountry || localCountry || "",
          }
        })
        setUserInfoWithBuddies(res)
      })
    }
  }, [requestedSuggestedBuddiesCount, suggestedBuddiesAtomData, user?.uid])

  useEffect(() => {
    const restrictedRoutes: (typeof ROUTES)[keyof typeof ROUTES][] = [
      ROUTES.PROFILE,
    ]

    if (
      !user?.uid &&
      restrictedRoutes.includes(
        window.location.pathname as (typeof ROUTES)[keyof typeof ROUTES],
      )
    ) {
      // redirect to letshang.info
      window.location.href = "https://letshang.info"
      return
    }
  }, [])

  const exploreFeatureFlagState = useProcessFeatureFlags(
    EFeatureFlags.EXPLORE_SECTION,
  )

  return (
    <QueryClientProvider client={queryClient}>
      <DevEmailIndicator />
      <BrowserRouter>
        <StripeProvider>
          <ToastContainer />
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path={ROUTES.LANDING} element={<LandingPage />} />
              <Route path={ROUTES.DOWNLOAD_APP} element={<DownloadApp />} />
              <Route path={ROUTES.GROUPS} element={<GroupLandingPage />} />
              <Route path={ROUTES.SIGNUP} element={<SignUpPage />} />
              <Route path={ROUTES.PROFILE} element={<ProfilePage />} />
              {/* <Route element={<FeatureFlagRestrictedRoutes />}> */}
              <Route path={ROUTES.EXPLORE} element={<Explore />} />
              {/* </Route> */}
              {/* <Route path={ROUTES.SEARCH} element={<SearchPage />} /> */}

              <Route path={ROUTES.SHARE_EVENTS} element={<RedirectTo />} />
              <Route path={ROUTES.EVENTS} element={<EventLandingPage />} />
              <Route path={ROUTES.CREATE_EVENT} element={<CreateEventPage />} />
              <Route
                path={ROUTES.CREATE_EVENT_FAST_SIGN_UP}
                element={<CreateEventPageV2 />}
              />
              <Route
                path={ROUTES.CREATE_EVENT_V3}
                element={<CreateEventPageV3 />}
              />
              <Route
                path={ROUTES.CREATE_EVENT_V4}
                element={<CreateEventPageV4 />}
              />
              <Route
                path={ROUTES.CREATE_EVENT_V5}
                element={<CreateEventPageV5 />}
              />
              {/* Organization routes */}
              <Route
                path={ROUTES.SHARE_ORGANIZATIONS}
                element={<RedirectTo />}
              />
              <Route
                path={ROUTES.ORGANIZATIONS}
                element={<OrganizationProfile />}
              />

              <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />
              <Route
                path={ROUTES.EVENT_SIGNUP}
                element={<ProtectedRouteWrapper element={<EventSignUp />} />}
              />
              <Route path={ROUTES.CHOOSE_TICKET} element={<ChooseTicket />} />
              <Route
                path={ROUTES.FILL_FRIENDS_TICKET_INFO}
                element={<FillFriendsTicketInfo />}
              />
              <Route
                path={ROUTES.FILL_QUESTIONNAIRE}
                element={
                  <ProtectedRouteWrapper element={<FillQuestionnaire />} />
                }
              />
              <Route
                path={ROUTES.FILL_QUESTIONNAIRE_UPDATED}
                element={
                  <ProtectedRouteWrapper
                    element={<FillQuestionnaireUpdated />}
                  />
                }
              />

              <Route
                path={`share${ROUTES.CLAIM_TICKET}`}
                element={<RedirectTo />}
              />

              <Route
                path={ROUTES.CLAIM_TICKET}
                element={<ClaimTicketLandingPage />}
              />
              <Route
                path={ROUTES.STRIPE_CHECKOUT}
                element={<ProtectedRouteWrapper element={<StripeCheckout />} />}
              />
              <Route
                path={ROUTES.STRIPE_SUCCESS}
                element={<ProtectedRouteWrapper element={<StripeSuccess />} />}
              />
              <Route
                path={ROUTES.EVENT_CREATE_SUCCESS}
                // element={<ProtectedRouteWrapper element={<StripeSuccess />} />}
                element={<EventCreateSuccess />}
              />

              <Route path={ROUTES.TICKETS} element={<MyEventTicket />}>
                <Route index element={<TicketCollection />} />
                <Route
                  path={ROUTES.MY_TICKET_ID}
                  element={<IndividualTicket />}
                />
              </Route>

              <Route path={ROUTES.EVENT_ROULLETE} element={<EventRoulette />} />
              <Route path={ROUTES.SPECIAL_EVENT} element={<SpecialEvent />} />
              {/* Redirect unknown routes to landing or any default route */}
              <Route path="*" element={<Navigate to={ROUTES.LANDING} />} />
              <Route path={ROUTES.PEOPLE} element={<PeoplePage />} />
              <Route
                path={ROUTES.EVENT_TEMPLATES}
                element={<EventTemplates />}
              />
              <Route
                path={ROUTES.COMPLETE_PROFILE}
                element={<CompleteProfile />}
              />
              <Route
                path={ROUTES.PLUGIN_STUDIO_CREATION}
                element={<PluginDevelopmentStudio />}
              />
              <Route
                path={ROUTES.PLUGIN_DETAILS}
                element={<PluginDetailPage />}
              />
              <Route path={ROUTES.EDIT_EVENT} element={<EditEvent />} />
            </Routes>
          </div>
        </StripeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default withProviders(App)
